import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useLanguage } from '@core/i18n';
import type { ServiceType } from '@features/booking/state/types';

const WaitingListAlternativesPage = () => {
  const { t } = useTranslation('booking');
  const { goToStep } = useBookingFlow();
  const booking = useBooking();
  const { dateLocale } = useLanguage();

  const alternatives = booking.wlAlternatives ?? [];

  // Select an alternative → switch to standard flow on that date+service.
  //
  // This is an instance of the step-self-invalidation class documented on
  // resolveNextStepAfter (flow/steps.ts): the flowType switch removes
  // WL_ALTERNATIVES from activeSteps while this page is mounted. That
  // primitive is deliberately NOT used here — it only looks forward, and the
  // step the guest must revisit (TIME) sits canonically BEFORE this one. The
  // explicit goToStep('TIME') below is the correct transition; a test in
  // steps.resolveNextStepAfter.test.ts pins this counterexample.
  // The user picks the hour in the standard TIME step (legacy goToHomeFromWlAlternatives
  // re-runs availability search; here the TIME step does the same lookup).
  // flushSync so the orchestrator recomputes active steps with flowType='standard'
  // before goToStep resolves. We jump to TIME explicitly: switching to standard drops
  // WL_ALTERNATIVES from the active steps, so the current index is -1 and goToNext()
  // would no-op.
  // updateBooking is a shallow merge, so any state set on the WL path (or back-nav
  // from /payment) leaks into the standard flow. We reset:
  //   - selectedTime: a prior pass may have left an hour; the user must re-pick.
  //   - timeSlots: the WL entry path left the original date's slots ([] since the
  //     date had no availability). The TIME step only self-loads when timeSlots is
  //     null (useBookingFlow needsDirectLoad = !timeSlots), so without this the
  //     alternative date renders the stale empty slot list and never re-fetches.
  //   - guaranteeType + feeDeposit: prevent the ConfirmationPage guard from false-
  //     positive on stale WL values when the picked alternative is a traditional slot.
  //   - cardToken + paymentMethod: the token was tokenized for a different intent
  //     (waitlist guarantee for the dates in WL_FORM). The user is now booking a
  //     specific alternative slot — re-tokenizing makes the orchestrator route
  //     through /payment so guaranteeType/feeDeposit get re-set from the slot data,
  //     which is what handleConfirm reads to choose the deposit vs traditional path.
  const handleSelectAlternative = (date: string, serviceType: string) => {
    flushSync(() => {
      booking.updateBooking({
        flowType: 'standard',
        selectedDate: date,
        service: serviceType as ServiceType,
        selectedTime: null,
        timeSlots: null,
        suggestedFromWaitingListAlternatives: true,
        guaranteeType: null,
        feeDeposit: 0,
        cardToken: null,
        paymentMethod: null,
      });
    });
    goToStep('TIME');
  };

  // Show alternatives (page only renders when alternatives exist via step condition)
  return (
    <BookingPageLayout
      title={t('NEW_MODAL_NO_NEED_TO_WAIT')}
      subtitle={t('NEW_MODAL_SELECT_ONE_OPTION_BELOW')}
    >
      <div className="w-full px-6 space-y-4">
        {alternatives.map((alt) => (
          <button
            key={`${alt.date}-${alt.serviceType}`}
            type="button"
            onClick={() => handleSelectAlternative(alt.date, alt.serviceType)}
            className="w-full text-left bg-foreground/5 rounded-xl p-4 hover:bg-brand hover:text-brand-foreground transition-colors"
          >
            <span className="block font-medium">
              {format(new Date(alt.date + 'T12:00:00'), 'EEEE, MMMM d', { locale: dateLocale })}
            </span>
            <span className="block text-sm opacity-50 capitalize">{alt.serviceType}</span>
          </button>
        ))}
      </div>
    </BookingPageLayout>
  );
};

export default WaitingListAlternativesPage;
